import { useState, useEffect } from 'react'
import { useSearchParams, useFetchers, Link } from 'react-router'
import {
  BackButton,
  Divider,
  FabricCardList,
  ImageCards,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '../../components'
import { useGarmentTypeFilters, useGlobalState } from '../../hooks'
import { getAvailabilityPropsFromApi } from '../../components/Chips/AvailabilityChip'

export function StyleflowCollection({ collection, fabrics }: StyleflowCollectionProps) {
  const { onMobile, onTablet, setToastInfo } = useGlobalState()
  const [params, setParams] = useSearchParams()
  const fetchers = useFetchers()
  const [view, setView] = useState(params.get('view') ?? SWATCHES)
  const [hasGarments] = useState(fabrics.length > 0)
  const isGarmentView = view === GARMENTS
  const { currentFabrics, GarmentFilters } = useGarmentTypeFilters({ fabrics })
  const isMobile = onMobile || onTablet
  const handleSetView = (view: string) => {
    setParams(params => {
      params.set('view', view)
      return params
    })
    setView(view)
  }

  useEffect(() => {
    const fetcher = fetchers.find(fetcher => fetcher.key === ImageCards.FETCHER_KEY)
    if (fetcher?.data) {
      setToastInfo({ show: true, severity: fetcher.data.error ? 'error' : 'success', message: fetcher?.data?.message })
    }
  }, [fetchers, setToastInfo])

  return (
    <Stack spacing={4} justifyContent={{ mobile: 'center', laptop: 'space-between' }}>
      <BackButton to='/styleflow/collections' />
      <Header collection={collection} />
      {isMobile && <ToggleView disabled={!hasGarments} view={view} setView={handleSetView} />}
      <Divider />
      <Stack direction='row' justifyContent={{ mobile: 'center', laptop: 'space-between' }}>
        {isGarmentView ? <GarmentFilters /> : <div />}
        {!isMobile && <ToggleView disabled={!hasGarments} view={view} setView={handleSetView} />}
      </Stack>
      <FabricImageList fabrics={currentFabrics} isGarmentView={isGarmentView} collectionFabrics={collection.fabrics} />
    </Stack>
  )
}

function Header({ collection }: HeaderProps) {
  return (
    <Stack direction='row' justifyContent={{ mobile: 'center', laptop: 'space-between' }}>
      <Stack direction={{ mobile: 'column', laptop: 'row' }} spacing={{ mobile: 1, laptop: 4 }} justifyContent='center'>
        <img src={collection.image} alt={collection.name} height={100} width={100} />
        <div>
          <Typography gutterBottom variant='h1'>
            {collection.brand ?? collection.title}
          </Typography>
          <Typography variant='body1'>{collection.title}</Typography>
        </div>
      </Stack>
    </Stack>
  )
}

function ToggleView({ view, setView, disabled = false }: ToggleViewProps) {
  return (
    <ToggleButtonGroup
      disabled={disabled}
      exclusive
      size='medium'
      value={view}
      onChange={(_e, v) => v && setView(v)}
      sx={{ width: 'fit-content', alignSelf: 'center' }}
    >
      <ToggleButton value={SWATCHES}>Swatches</ToggleButton>
      <ToggleButton value={GARMENTS}>Garments</ToggleButton>
    </ToggleButtonGroup>
  )
}

function FabricImageList({ fabrics, collectionFabrics, isGarmentView = false }: FabricImageListProps) {
  const collectionId = collectionFabrics[0]?.collectionId ?? ''

  if (fabrics.length < 1 && collectionFabrics.length < 1) {
    return (
      <Typography variant='h6' align='center' width={1} pt={4}>
        No Images Found For This Collection
      </Typography>
    )
  }

  if (isGarmentView) {
    const items = fabrics.map(fabric => {
      const fabricPosition = fabric?.position ? ` (${fabric.position})` : ''
      return {
        key: fabric.id,
        title: {
          text: `${fabric.fabric?.trinityNumber}${fabricPosition}`,
          tooltip: 'See in Fabric Explorer',
          link: fabric.fabric ? `/fabric-explorer/fabrics/${fabric.fabric?.id}` : undefined,
        },
        image: {
          uri: fabric.image,
          description: `${fabric.baseModel.description} - ${fabric.fabric?.description}`,
          zoom_uri: fabric.picarioUri,
        },
        stock: getAvailabilityPropsFromApi(fabric.fabric?.stockStatus),
        actions: [
          {
            type: 'add',
            title: 'Add to Rack',
            intent: 'add',
          } as const,
          {
            type: 'edit',
            title: 'Edit',
            intent: 'edit',
          } as const,
        ],
        item: fabric,
      }
    })
    return (
      <ImageCards items={items}>
        {item => (
          <Stack p={2}>
            <input type='hidden' name='baseModelId' value={item.baseModel.id} />
            <input type='hidden' name='fabricId' value={item.fabric?.id} />
            <input type='hidden' name='garmentType' value={item.garmentType.bitmask} />
            <input type='hidden' name='imageUri' value={item.picarioUri} />
            <input type='hidden' name='liningId' value={item.lining?.id} />
            <input type='hidden' name='buttonId' value={item.button?.id} />
            <input type='hidden' name='threadId' value={item.thread?.id} />
            <input type='hidden' name='ribknitId' value={item.ribknit?.id} />
            <input type='hidden' name='zipperId' value={item.zipper?.id} />
            <input type='hidden' name='collectionId' value={collectionId} />
            <input type='hidden' name='model' value={item.baseModel.description} />
            <Stack
              flex={1}
              spacing={2}
              pb={2}
              sx={{ '& .MaterialRow:not(:last-child)': { borderBottom: 1, borderColor: 'grey.200' } }}
            >
              {item.fabric && (
                <MaterialRow
                  label='Fabric'
                  description={item.fabric.description}
                  link={`/fabric-explorer/fabrics/${item.fabric.id}`}
                />
              )}
              <MaterialRow label='Model' description={item.baseModel.description} />
              {item.lining && (
                <MaterialRow
                  label='Lining'
                  description={item.lining.trinityNumber}
                  link={`/fabric-explorer/fabrics/${item.lining.id}`}
                />
              )}
              {item.button && <MaterialRow label='Button' description={item.button.description} />}
              {item.thread && <MaterialRow label='Thread' description={item.thread.description} />}
              {item.ribknit && <MaterialRow label='Ribknit' description={item.ribknit.description} />}
              {item.zipper && <MaterialRow label='Zipper' description={item.zipper.description} />}
            </Stack>
          </Stack>
        )}
      </ImageCards>
    )
  }

  const swatchFabrics = collectionFabrics.map(fabric => {
    const fabricImage = fabrics.find(f => f.fabric?.id === fabric.id)
    return { ...fabric, position: fabricImage?.position }
  })
  const firstFabric = fabrics[0]
  const baseModelId = firstFabric?.baseModel.id
  const garmentType = firstFabric?.garmentType?.bitmask

  return (
    <FabricCardList
      fabrics={swatchFabrics}
      baseUrl={`/styleflow/visualize?fabricId=&baseModelId=${baseModelId}&garmentType=${garmentType}&referrer=collections/${collectionId}`}
    />
  )
}

//* HELPERS
const SWATCHES = 'swatches'
const GARMENTS = 'garments'

interface MaterialRowProps {
  label: string
  description: string
  link?: string
}
const MaterialRow = ({ label, description, link = undefined }: MaterialRowProps) => {
  return (
    <Stack direction='row' justifyContent='space-between' className='MaterialRow'>
      <Typography variant='smallBody2'>{label}</Typography>
      <Tooltip title={description}>
        <Typography
          variant='smallBody1'
          noWrap
          width='10rem'
          align='right'
          sx={{ '& a': { color: 'primary.main', textDecoration: 'none' } }}
        >
          {link ? <Link to={link}>{description}</Link> : <>{description}</>}
        </Typography>
      </Tooltip>
    </Stack>
  )
}

//* TYPES
interface StyleflowCollectionProps {
  collection: TrinityAPI.CollectionExtendedType
  fabrics: TrinityAPI.FabricImageType[]
}

type FabricsPropType = Pick<StyleflowCollectionProps, 'fabrics'>
type HeaderProps = Pick<StyleflowCollectionProps, 'collection'>

interface ToggleViewProps {
  view: string
  setView: (view: string) => void
  disabled?: boolean
}

interface FabricImageListProps extends FabricsPropType {
  collectionFabrics: TrinityAPI.FabricExtendedType[]
  isGarmentView?: boolean
}
